import React from 'react'
import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { useSavingsStore } from '@/store/useSavingsStore'
import { currencyUtils } from '@/utils'
import { useTheme } from '@/hooks/useTheme'

const { formatDynamicCurrency } = currencyUtils

export const TransferInAmountWithSwap: React.FC = () => {
  const theme = useTheme()
  const isFiatMode = useSavingsStore((state) => state.goalTransferIn.isFiatMode)
  const fiatAmount = useSavingsStore((state) => state.goalTransferIn.fiatAmount)
  const cryptoAmount = useSavingsStore(
    (state) => state.goalTransferIn.cryptoAmount,
  )
  const triggerShake = useSavingsStore(
    (state) => state.goalTransferIn.triggerShake,
  )
  const setGoalTransferIn = useSavingsStore((state) => state.setGoalTransferIn)

  // Основная отображаемая сумма - по умолчанию USDT (криптовалюта)
  const primaryAmount = isFiatMode ? fiatAmount : cryptoAmount
  const displayAmount = primaryAmount.amount || '0'

  // Альтернативная сумма - по умолчанию фиатная валюта
  const alternativeAmount = isFiatMode ? cryptoAmount : fiatAmount

  // Форматируем альтернативную сумму для отображения
  const formattedAlternativeAmount = formatDynamicCurrency({
    amount: alternativeAmount.amount,
    currencyCode: alternativeAmount.code,
    showDecimals: true,
  })

  // Вычисляем размер шрифта как в React Native (amountInput)
  const getFontSizeForAmount = (text: string) => {
    const fontSizeSnapPoints: { maxLength: number; fontSize: number }[] = [
      { maxLength: 8, fontSize: 64 },
      { maxLength: 9, fontSize: 54 },
      { maxLength: 12, fontSize: 44 },
      { maxLength: 15, fontSize: 34 },
      { maxLength: Infinity, fontSize: 24 }, // For anything above 15
    ]

    // Находим первый snap point где длина текста меньше или равна лимиту
    const fontSize =
      fontSizeSnapPoints.find(({ maxLength }) => text.length <= maxLength)
        ?.fontSize ?? 20 // Fallback

    return fontSize
  }

  // Обработчик переключения режима валют
  const handleSwapCurrency = () => {
    setGoalTransferIn({ isFiatMode: !isFiatMode })
  }

  return (
    <BaseView center gap={16}>
      {/* Основная сумма */}
      <BaseView
        center
        style={{
          minHeight: 74,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'baseline',
          justifyContent: 'center',
          gap: 4,
        }}
        className={triggerShake ? 'animate-shake' : ''}
      >
        <BaseText
          fontFamily="bold"
          color="label.default"
          style={{
            fontSize: `${getFontSizeForAmount(displayAmount)}px`,
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}
        >
          {displayAmount}
        </BaseText>

        {/* Отображаем код валюты рядом с суммой */}
        <BaseText
          fontFamily="bold"
          color="label.secondary"
          style={{
            fontSize: '20px',
            lineHeight: 1,
          }}
        >
          {primaryAmount.code}
        </BaseText>
      </BaseView>

      {/* Альтернативная сумма с кнопкой переключения */}
      <BaseView
        row
        center
        gap={8}
        className="cursor-pointer hover:opacity-80 active:opacity-60 transition-all duration-200 select-none"
        onClick={handleSwapCurrency}
        style={{
          padding: '6px 12px',
          borderRadius: '8px',
          minHeight: 36,
        }}
      >
        <BaseText
          fontSize={18}
          fontFamily="bold"
          color="label.default"
          textAlign="center"
          style={{
            userSelect: 'none',
          }}
        >
          {formattedAlternativeAmount}
        </BaseText>

        <AppIcons name="switch" size={18} color="icon.primary" />
      </BaseView>
    </BaseView>
  )
}
